import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Trailers, played so they look like Zflix rather than a YouTube page (Zach, 2026-11-03): YouTube's player with all
 * of its own buttons off, under Zflix's title and progress bar, which also cover where YouTube puts its title and
 * logo. Taps and the remote go to Zflix (OK pause, Left/Right 10 s, Back closes). It closes just before the end, so
 * YouTube's "more videos" screen never shows.
 */
let apiReady = null;
function loadApi() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (apiReady) return apiReady;
  apiReady = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve();
    };
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    s.onerror = () => {
      apiReady = null;
      reject(new Error('Trailer could not load'));
    };
    document.head.appendChild(s);
  });
  return apiReady;
}

/**
 * The Fire TV app. Its web view shows a playing video as black when anything around it clips with rounded corners
 * (border-radius with overflow hidden or scroll): tested on the Family Room TV 2026-11-05, the clock ran but the
 * picture stayed black, and the same player with square corners showed the picture (clip-path rounding is black too).
 * So on the TV, trailers and the boxes around them clip square and get their round corners from RoundCorners.
 */
export function isTvApp() {
  return (typeof window !== 'undefined' && window.__zflixTvClient === true) || (typeof navigator !== 'undefined' && /ZFLIX-TV/.test(navigator.userAgent || ''));
}

/**
 * Rounded corners for the TV without a rounded clip (Zach, 2026-11-05: square corners look bad). Each corner is a
 * small square painted in the color behind the box, with a clear quarter circle cut out, laid over the picture.
 * Proven on the Family Room TV: the trailer plays under it with the corners still round.
 */
export function RoundCorners({ r = 12, color = 'var(--bg)', top = true, bottom = true }) {
  const list = [];
  if (top) list.push(['top', 'left', '100% 100%'], ['top', 'right', '0 100%']);
  if (bottom) list.push(['bottom', 'left', '100% 0'], ['bottom', 'right', '0 0']);
  return list.map(([v, h, at]) => (
    <div
      key={v + h}
      aria-hidden="true"
      style={{ position: 'absolute', [v]: 0, [h]: 0, width: r, height: r, zIndex: 7, pointerEvents: 'none', background: `radial-gradient(circle at ${at}, transparent ${r - 0.5}px, ${color} ${r}px)` }}
    />
  ));
}

/** { key, name } for this title, or null when there is none. */
export function useTrailer(item) {
  const [t, setT] = useState(null);
  const id = item?.source === 'jellyfin' ? item.id : '';
  const tmdbId = item?.tmdbId || '';
  const type = item?.type === 'movie' ? 'movie' : 'show';
  useEffect(() => {
    setT(null);
    if (!id && !tmdbId) return undefined;
    let dead = false;
    const q = id ? `id=${encodeURIComponent(id)}` : `tmdbId=${encodeURIComponent(tmdbId)}&type=${type}`;
    fetch(`/api/trailer?${q}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => !dead && setT(j && j.key ? j : null))
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, [id, tmdbId, type]);
  return t;
}

/**
 * Trailers play with no captions (Zach, 2026-11-03: they all still had them). YouTube turns them on by itself for many
 * videos once playback starts; clearing the track and unloading the captions module turns them off.
 */
function captionsOff(p) {
  try {
    p.setOption('captions', 'track', {});
  } catch (_) {}
  try {
    p.setOption('cc', 'track', {});
  } catch (_) {}
  try {
    p.unloadModule('captions');
  } catch (_) {}
  try {
    p.unloadModule('cc');
  } catch (_) {}
}

/**
 * The trailer playing quietly behind a title's picture on its details (Zach, 2026-11-04). Starts after a moment,
 * muted, cropped so YouTube's title and logo fall outside the frame, and fades back to the picture before the end.
 * Tapping it opens the full trailer with sound.
 */
export function BackdropTrailer({ trailer, paused, onTap, radius = '16px 16px 0 0', label = '▶ Trailer · tap for sound', yieldTo = '', poster = '' }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const [on, setOn] = useState(false);
  /** true: the last hide was instant (no fade), because YouTube had stopped and would show its own icons. */
  const [fast, setFast] = useState(false);
  /** No "tap for sound" on the TV (Zach, 2026-11-05): the remote has its own Trailer button. */
  const onTv = isTvApp();
  const shownLabel = onTv ? '' : label;
  /**
   * On the TV, YouTube's own icons (its pause sign) show through even when the player is fully see-through: caught
   * on a screen recording 2026-11-05. So there the player stays opaque and a solid copy of the poster sits on top of
   * it until the trailer is really playing.
   */
  const covered = onTv && !!poster;
  const fade = !on && fast ? 'none' : 'opacity 0.8s ease';
  useEffect(() => {
    if (!trailer || !trailer.key || paused) return undefined;
    let dead = false;
    let startedAt = null;
    let shown = false;
    /** Reached the end on purpose: stays on the picture. */
    let finished = false;
    /**
     * Gone at once whenever YouTube is not really playing (paused when the app went to the background, loading,
     * ended): its pause icon must never be seen (Zach, 2026-11-05). It comes back after 1.5 s of real playback.
     */
    const hideNow = () => {
      if (!shown || dead) return;
      shown = false;
      startedAt = null;
      setFast(true);
      setOn(false);
    };
    const onVisibility = () => {
      if (document.hidden) hideNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    const start = setTimeout(() => {
      loadApi()
        .then(() => {
          if (dead || !hostRef.current) return;
          playerRef.current = new window.YT.Player(hostRef.current, {
            host: 'https://www.youtube-nocookie.com',
            videoId: trailer.key,
            width: '100%',
            height: '100%',
            playerVars: { autoplay: 1, mute: 1, controls: 0, disablekb: 1, fs: 0, iv_load_policy: 3, modestbranding: 1, rel: 0, playsinline: 1, cc_load_policy: 0, origin: window.location.origin },
            events: {
              onReady: (e) => {
                captionsOff(e.target);
                e.target.mute();
                e.target.playVideo();
              },
              onStateChange: (e) => {
                if (e.data === 1) {
                  captionsOff(e.target);
                  setTimeout(() => captionsOff(e.target), 800);
                  /** Shown only once the picture is really moving (see the poll below), so YouTube's own icons never show. */
                  if (startedAt == null) startedAt = e.target.getCurrentTime() || 0;
                } else hideNow();
              },
              onError: () => hideNow(),
            },
          });
        })
        .catch(() => {});
    }, 1500);
    let yielded = false;
    /** Faded out first (0.8 s), paused only once it is out of sight: a paused YouTube frame shows its pause icon. */
    const hideThenPause = (p) => {
      shown = false;
      setFast(false);
      setOn(false);
      setTimeout(() => {
        try {
          if (!shown) p.pauseVideo();
        } catch (_) {}
      }, 900);
    };
    const iv = setInterval(() => {
      const p = playerRef.current;
      if (!p || !p.getDuration) return;
      /** Something on top (a video, a title's details): pause and hide; carry on when it closes. */
      if (yieldTo) {
        const covered = !!document.querySelector(yieldTo);
        if (covered && !yielded) {
          yielded = true;
          hideThenPause(p);
          return;
        }
        if (!covered && yielded) {
          yielded = false;
          startedAt = null;
          try {
            p.playVideo();
          } catch (_) {}
        }
        if (covered) return;
      }
      if (document.hidden) return;
      const st = p.getPlayerState ? p.getPlayerState() : -1;
      const d = p.getDuration() || 0;
      const at = p.getCurrentTime() || 0;
      if (shown && st !== 1) hideNow();
      /** Paused by the TV while Zflix was in the background: carry on, out of sight. */
      if (!shown && !finished && !yielded && st === 2) {
        try {
          p.playVideo();
        } catch (_) {}
      }
      /** 1.5 s of real playback before it shows, so YouTube's start-up icons and title are gone by then. */
      if (!shown && !finished && startedAt != null && at - startedAt >= 1.5 && st === 1) {
        shown = true;
        setFast(false);
        setOn(true);
      }
      if (d > 5 && d - at < 1.5 && shown) {
        finished = true;
        hideThenPause(p);
      }
    }, 500);
    return () => {
      dead = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(start);
      clearInterval(iv);
      setOn(false);
      try {
        playerRef.current?.destroy();
      } catch (_) {}
      playerRef.current = null;
    };
  }, [trailer, paused, yieldTo]);
  return (
    <div
      data-zflix-backdrop-trailer
      onClick={on ? onTap : undefined}
      style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: onTv ? 0 : radius, zIndex: 1, opacity: on || covered ? 1 : 0, transition: fade, pointerEvents: on ? 'auto' : 'none', cursor: 'pointer', background: '#000' }}
    >
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: '125%', aspectRatio: '16 / 9', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
        <div ref={hostRef} style={{ width: '100%', height: '100%' }} />
      </div>
      {covered ? (
        <img src={poster} alt="" aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', zIndex: 1, pointerEvents: 'none', opacity: on ? 0 : 1, transition: fade }} />
      ) : null}
      {on && shownLabel ? (
        <div style={{ position: 'absolute', top: 14, left: 14, padding: '5px 10px', borderRadius: 999, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 12, fontWeight: 700, pointerEvents: 'none' }}>
          {shownLabel}
        </div>
      ) : null}
    </div>
  );
}

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export function TrailerPlayer({ trailer, title, onClose }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [t, setT] = useState({ at: 0, dur: 0, paused: false, ready: false });
  const [error, setError] = useState('');

  useEffect(() => {
    let dead = false;
    let poll = null;
    loadApi()
      .then(() => {
        if (dead) return;
        playerRef.current = new window.YT.Player(hostRef.current, {
          host: 'https://www.youtube-nocookie.com',
          videoId: trailer.key,
          width: '100%',
          height: '100%',
          playerVars: { autoplay: 1, controls: 0, disablekb: 1, fs: 0, iv_load_policy: 3, modestbranding: 1, rel: 0, playsinline: 1, cc_load_policy: 0, origin: window.location.origin },
          events: {
            onReady: (e) => {
              captionsOff(e.target);
              e.target.playVideo();
              poll = setInterval(() => {
                const p = playerRef.current;
                if (!p || !p.getDuration) return;
                const dur = p.getDuration() || 0;
                const at = p.getCurrentTime() || 0;
                setT({ at, dur, paused: p.getPlayerState() === 2, ready: true });
                /** The first 8 seconds: if captions came back on, off again. */
                if (at < 8) {
                  try {
                    const tr = p.getOption && p.getOption('captions', 'track');
                    if (tr && tr.languageCode) captionsOff(p);
                  } catch (_) {}
                }
                /** Gone before YouTube's end screen of other videos. */
                if (dur > 5 && dur - at < 0.6) closeRef.current();
              }, 250);
            },
            onStateChange: (e) => {
              if (e.data === 0) closeRef.current();
              /** Playing: YouTube loads its captions only now, so they come off now (and once more just after). */
              if (e.data === 1) {
                captionsOff(e.target);
                setTimeout(() => captionsOff(e.target), 800);
                setTimeout(() => captionsOff(e.target), 2500);
              }
            },
            onApiChange: (e) => captionsOff(e.target),
            onError: () => setError('This trailer cannot be played here.'),
          },
        });
      })
      .catch((e) => setError(e.message));
    return () => {
      dead = true;
      clearInterval(poll);
      try {
        playerRef.current?.destroy();
      } catch (_) {}
    };
  }, [trailer.key]);

  const toggle = () => {
    const p = playerRef.current;
    if (!p || !p.getPlayerState) return;
    if (p.getPlayerState() === 1) p.pauseVideo();
    else p.playVideo();
  };
  const jump = (d) => {
    const p = playerRef.current;
    if (p && p.getCurrentTime) p.seekTo(Math.max(0, p.getCurrentTime() + d), true);
  };

  /** The remote: tvSpatialNav hands every key here first while the trailer is up (like Up next). */
  useEffect(() => {
    const prevKeys = window.__zflixTopKeys;
    const prevBack = window.__zflixOwnBack;
    window.__zflixTopKeys = (e) => {
      const k = e.key;
      if (k === 'Enter' || k === ' ' || k === 'MediaPlayPause') toggle();
      else if (k === 'ArrowLeft' || k === 'MediaRewind') jump(-10);
      else if (k === 'ArrowRight' || k === 'MediaFastForward') jump(10);
      else if (k === 'Escape' || k === 'GoBack' || k === 'BrowserBack' || k === 'Backspace') closeRef.current();
      return true;
    };
    window.__zflixOwnBack = () => {
      closeRef.current();
      return true;
    };
    return () => {
      if (prevKeys) window.__zflixTopKeys = prevKeys;
      else delete window.__zflixTopKeys;
      if (prevBack) window.__zflixOwnBack = prevBack;
      else delete window.__zflixOwnBack;
    };
  }, []);

  const pct = t.dur ? Math.min(100, (t.at / t.dur) * 100) : 0;
  return ReactDOM.createPortal(
    <div data-zflix-trailer style={{ position: 'fixed', inset: 0, zIndex: 100002, background: '#000', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div ref={hostRef} style={{ width: '100%', height: '100%' }} />
      </div>
      {/* Everything YouTube would show is under this: taps reach Zflix, not YouTube. */}
      <div onClick={toggle} style={{ position: 'absolute', inset: 0, cursor: 'pointer' }} />
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 120, background: 'linear-gradient(to bottom, rgba(0,0,0,0.95), rgba(0,0,0,0.75) 45%, transparent)', display: 'flex', alignItems: 'flex-start', padding: '22px 28px', pointerEvents: 'none' }}>
        <div style={{ color: '#fff', minWidth: 0 }}>
          <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#e50914', fontWeight: 800 }}>Trailer</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        </div>
      </div>
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close trailer" style={{ position: 'absolute', top: 18, right: 22, width: 44, height: 44, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 22, cursor: 'pointer' }}>✕</button>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 110, background: 'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.75) 50%, transparent)', padding: '0 28px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pointerEvents: 'none' }}>
        <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.25)', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: '#e50914' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, color: '#ddd', fontSize: 13 }}>
          <span>{t.paused ? 'Paused' : ''}</span>
          <span>{t.dur ? `${fmt(t.at)} / ${fmt(t.dur)}` : ''}</span>
        </div>
      </div>
      {!t.ready && !error ? (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <style>{'@keyframes zflix-spin{to{transform:rotate(360deg)}}'}</style>
          <div style={{ width: 56, height: 56, borderRadius: '50%', border: '5px solid rgba(255,255,255,0.22)', borderTopColor: '#e50914', animation: 'zflix-spin 0.9s linear infinite' }} />
        </div>
      ) : null}
      {error ? (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, pointerEvents: 'none' }}>{error}</div>
      ) : null}
    </div>,
    document.body,
  );
}
